import React, { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { compactPageItems } from "../../components/ui/compactPagination";
import { getSerialNumber } from "../../utils/pagination";
import "../admins/AdminStaffPage.css";
import "./ManagerTeamUsersPage.css";
import { avatarTone, roleKey, Empty } from "./teamUserDisplay";

// Manager -> Team List: READ-ONLY directory of the manager's own unit.
// GET /manager/team-list (+ /filters) -- the backend checks users:view_team
// and scopes every query to the caller's own unit (services/
// managerUserService.js). This page never sends a unit id, and has no
// create/edit/activate/deactivate actions -- that is Team Users.

const PAGE_SIZE = 10;

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

const ManagerTeamListPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reportsFilter, setReportsFilter] = useState(""); // "" = whole unit, "direct"
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // Filter choices come from the backend, already limited to the own unit.
  const [filters, setFilters] = useState({ unit: null, roles: [], departments: [], directReports: 0 });

  // Debounced search -> server query.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const res = await api.get(`/manager/team-list`, {
        params: {
          page,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          role_id: roleFilter || undefined,
          department_id: departmentFilter || undefined,
          is_active: statusFilter || undefined,
          reports: reportsFilter || undefined,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.meta?.total) || 0);
    } catch (err) {
      console.error("Fetch team list failed", err);
      setLoadError(errorMessage(err, "Failed to load team list"));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, departmentFilter, statusFilter, reportsFilter]);

  const fetchFilters = useCallback(() => {
    api
      .get(`/manager/team-list/filters`)
      .then((res) => {
        const d = res.data?.data || {};
        setFilters({
          unit: d.unit || null,
          roles: d.roles || [],
          departments: d.departments || [],
          directReports: Number(d.directReports) || 0,
        });
      })
      // Non-fatal: the list still works, the dropdowns just stay at "All".
      .catch((err) => console.error("Fetch team list filters failed", err));
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const refresh = () => {
    fetchTeam();
    fetchFilters();
  };

  const onFilter = (setter) => (e) => {
    setPage(1);
    setter(e.target.value);
  };

  const anyFilter = !!(search || roleFilter || departmentFilter || statusFilter || reportsFilter);
  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="unit-page admin-tasks-theme team-users-page team-list-page">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Manager</div>
          <h2>Team List</h2>
          <p>
            Read-only directory of everyone in your unit
            {filters.unit?.name ? (
              <>
                {" "}
                &middot; <strong>{filters.unit.name}</strong>
              </>
            ) : null}
            .
          </p>
        </div>

        <div className="unit-header-actions">
          <button className="unit-btn unit-btn-light" onClick={refresh} disabled={loading}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </button>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-address-book staff-list-icon" aria-hidden="true"></i>
              Team Directory
            </h5>
            <p>Only users in your own unit are shown.</p>
          </div>

          <div className="unit-toolbar-actions">
            <div className="input-group unit-search">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </span>
              <input
                className="form-control"
                placeholder="Search name, username, email, phone, employee code..."
                aria-label="Search team members"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <select className="form-select staff-filter" aria-label="Filter by team scope" value={reportsFilter} onChange={onFilter(setReportsFilter)}>
              <option value="">All Unit Team</option>
              <option value="direct">Direct Reports ({filters.directReports})</option>
            </select>

            <select className="form-select staff-filter" aria-label="Filter by role" value={roleFilter} onChange={onFilter(setRoleFilter)}>
              <option value="">All Roles</option>
              {filters.roles.map((r) => (
                <option key={r.id} value={r.id} className="text-capitalize">
                  {String(r.name || "").replace(/_/g, " ")}
                </option>
              ))}
            </select>

            {filters.departments.length > 0 && (
              <select
                className="form-select staff-filter"
                aria-label="Filter by department"
                value={departmentFilter}
                onChange={onFilter(setDepartmentFilter)}
              >
                <option value="">All Departments</option>
                {filters.departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            )}

            <select className="form-select staff-filter" aria-label="Filter by status" value={statusFilter} onChange={onFilter(setStatusFilter)}>
              <option value="">All Statuses</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <div className="spinner-border" role="status"></div>
              <p>Loading team list...</p>
            </div>
          ) : loadError ? (
            <div className="unit-empty" role="alert">
              <div className="unit-empty-icon">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
              </div>
              <h5>Could not load team list</h5>
              <p>{loadError}</p>
              <button className="unit-btn unit-btn-light" onClick={refresh}>
                Retry
              </button>
            </div>
          ) : (
            <table className="table unit-table">
              <thead>
                <tr>
                  <th>S.NO.</th>
                  <th>Employee</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Reporting Manager</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((user, index) => (
                    <tr key={user.id} className={user.is_active ? "" : "tu-row-inactive"}>
                      <td className="tu-sno">{getSerialNumber(index, page, PAGE_SIZE)}</td>
                      <td>
                        <div className="unit-name-box">
                          <div className={`unit-letter tu-avatar tu-avatar-${avatarTone(user.name)}`}>
                            {user.name?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                          <div className="tu-name-text">
                            <strong>{user.name}</strong>
                            {user.employee_code && <small>{user.employee_code}</small>}
                          </div>
                        </div>
                      </td>
                      <td>{user.username ? <span className="tu-username">{user.username}</span> : <Empty />}</td>
                      <td title={user.email || ""}>
                        {user.email ? <span className="tu-email">{user.email}</span> : <Empty />}
                      </td>
                      <td>{user.phone_no ? <span className="tu-phone">{user.phone_no}</span> : <Empty />}</td>
                      <td>
                        {user.role_name ? (
                          <span className={`tu-role tu-role-${roleKey(user.role_name)}`}>{user.role_name.replace(/_/g, " ")}</span>
                        ) : (
                          <Empty />
                        )}
                      </td>
                      <td>{user.department_name || <Empty />}</td>
                      <td>{user.designation_name || <Empty />}</td>
                      <td>{user.reporting_manager_name || <Empty />}</td>
                      <td>
                        <span className={`tu-status ${user.is_active ? "tu-status-active" : "tu-status-inactive"}`}>
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-users" aria-hidden="true"></i>
                        </div>
                        <h5>No team members found in your unit.</h5>
                        <p>{anyFilter ? "Try another keyword or filter." : "Users added to your unit will appear here."}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing <strong>{rangeStart}</strong> to <strong>{rangeEnd}</strong> of <strong>{total}</strong> entries
          </div>

          {totalPages > 1 && (
            <ul className="pagination unit-pagination">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>
                  Prev
                </button>
              </li>
              {compactPageItems(
                [...Array(totalPages)].map((_, idx) => (
                  <li key={idx} className={`page-item ${page === idx + 1 ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setPage(idx + 1)}>
                      {idx + 1}
                    </button>
                  </li>
                )),
                page
              )}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>
                  Next
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerTeamListPage;
